import { useState } from "react";
import { motion } from "framer-motion";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, EASE } from "../lib/site";

const INFO = [
  { icon: MapPin, label: "Headquarters", value: "24 Hope Avenue, Suite 300, Newark, NJ 07102" },
  { icon: Mail, label: "Email", value: "hello@daio-hope.org" },
  { icon: Phone, label: "Phone", value: "+1 (973) 555-0148" },
  { icon: Clock, label: "Office hours", value: "Mon–Fri, 9:00 AM – 6:00 PM ET" },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received", {
        description: "Thank you for reaching out — we reply within one business day.",
      });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Message could not be sent", {
        description: "Please try again in a moment.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-linen/70 py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <p className="eyebrow">Get in touch</p>
          <h2 className="mt-5 font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl">
            Partner, volunteer, <em className="text-brand">or simply say hello.</em>
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-inksoft">
            Whether you're a foundation exploring a grant, a company planning a
            matching campaign, or a neighbor with an hour to give — our door in
            Newark is open.
          </p>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {INFO.map((item) => (
              <div key={item.label} className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand">
                  <item.icon className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-inksoft">
                    {item.label}
                  </p>
                  <p className="mt-1 text-sm font-medium leading-snug text-ink">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.form
          data-testid="form-contact-daio"
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="rounded-[32px] border border-line bg-cardx p-8 shadow-xl sm:p-10"
        >
          <h3 className="font-serif text-2xl font-semibold text-ink">Send us a message</h3>
          <div className="mt-6 space-y-3">
            <input
              data-testid="input-contact-name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Full name"
              className="input-field"
            />
            <input
              data-testid="input-contact-email"
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email address"
              className="input-field"
            />
            <textarea
              data-testid="input-contact-message"
              required
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="How would you like to help?"
              className="input-field resize-none"
            />
            <button
              data-testid="btn-submit-contact"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-brand py-3.5 text-sm font-semibold uppercase tracking-[0.15em] text-[#FDFBF7] transition-colors hover:bg-branddeep disabled:opacity-50"
            >
              {loading ? "Sending…" : "Send message"}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
